/**
 * MetricDonut — Dona compacta de las tarjetas KPI.
 *
 * QUÉ HACE: dibuja un anillo proporcional a los segmentos (con la cifra total
 *   en el centro) y una leyenda corta con el color, la etiqueta y el valor de
 *   cada porción. El anillo crece desde 0 al montar y migra al cambiar los
 *   valores (los segmentos entran escalonados). La usa la prop `donut` de
 *   MetricCard (Alertas Activas: severidad de las alertas).
 * QUÉ INVOCA: `useAnimatedSeries` (relleno animado de los segmentos).
 * QUÉ EXPORTA: MetricDonut, MetricDonutProps.
 * QUÉ NO DEBE HACER: no calcula los datos ni accede a almacenamiento.
 */

import { useAnimatedSeries } from "@/hooks/useAnimatedSeries";

/** Porción de la dona. */
interface MetricDonutSegment {
  label: string;
  value: number;
  /** Color del segmento (hex, p. ej. "#ef4444"). */
  color: string;
}

export interface MetricDonutProps {
  segments: MetricDonutSegment[];
  /**
   * Texto central. Si se omite, se muestra la suma animada de los segmentos
   * (efecto de conteo).
   */
  centerValue?: string;
  /** Etiqueta bajo la cifra central (p. ej. "activas"). */
  centerLabel?: string;
  /** Muestra la leyenda con etiqueta y valor (por defecto `true`). */
  showLegend?: boolean;
  /** Formatea el valor de cada segmento en la leyenda (por defecto, crudo). */
  valueFormatter?: (value: number) => string;
  className?: string;
}

/** Geometría del anillo: circunferencia ≈ 100 (`pathLength` normaliza el arco). */
const RADIUS = 15.9155;
const CENTER = 21;

export const MetricDonut = ({
  segments,
  centerValue,
  centerLabel,
  showLegend = true,
  valueFormatter = (value) => `${value}`,
  className = "",
}: MetricDonutProps) => {
  const animated = useAnimatedSeries(segments, {
    keys: ["value"],
    stagger: 90,
  });
  const targetTotal = segments.reduce(
    (sum, segment) => sum + Math.max(0, segment.value),
    0,
  );
  const animatedTotal = animated.reduce(
    (sum, segment) => sum + Math.max(0, segment.value),
    0,
  );
  const safeTotal = targetTotal > 0 ? targetTotal : 1;
  const displayCenter = centerValue ?? `${Math.round(animatedTotal)}`;
  let cumulative = 0;

  return (
    <div
      className={`flex min-h-0 flex-1 items-center justify-center gap-3 ${className}`}
    >
      <div className="relative h-[88px] w-[88px] shrink-0">
        <svg
          viewBox="0 0 42 42"
          className="h-full w-full -rotate-90"
          role="img"
          aria-label={`Distribución de ${targetTotal} elementos`}
        >
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={7}
            pathLength={100}
            className="text-slate-100 dark:text-slate-700"
          />
          {animated.map((segment) => {
            const pct = (Math.max(0, segment.value) / safeTotal) * 100;
            const offset = -cumulative;
            cumulative += pct;
            return (
              <circle
                key={segment.label}
                cx={CENTER}
                cy={CENTER}
                r={RADIUS}
                fill="none"
                stroke={segment.color}
                strokeWidth={7}
                pathLength={100}
                strokeDasharray={`${pct} ${100 - pct}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-extrabold leading-none text-slate-800 dark:text-slate-100">
            {displayCenter}
          </span>
          {centerLabel && (
            <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {centerLabel}
            </span>
          )}
        </div>
      </div>

      {showLegend && (
        <ul className="flex min-w-0 flex-col gap-1">
          {segments.map((segment) => (
            <li
              key={segment.label}
              className="flex min-w-0 items-center gap-1.5"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate text-[9px] font-medium text-slate-600 dark:text-slate-300">
                {segment.label}
              </span>
              <span className="shrink-0 text-[10px] font-bold tabular-nums text-slate-700 dark:text-slate-200">
                {valueFormatter(segment.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
